import {useCallback, useRef, useState} from 'react';

import {debounce} from 'lodash';

import {trackEvent} from '~/shared/services/analytics';
import {SettingsAction} from '~/shared/services/analytics/analyticsModels';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {SettingControllerName} from '~/shared/types/personalArea';

const DEBOUNCE_DELAY_MS = 250;

type ApiHandlerArgs = {
  request: (isEnabled: boolean) => Promise<void>;
  isEnabled: boolean;
  isEnabledBeforeInteraction: boolean;
  onBeforeRequest?: (acceptedIsActive: boolean) => void;
  onError?: (error: any) => void;
  onSuccess?: (isRequestSent: boolean) => void;
};

const handleApiRequest = debounce(async (args: ApiHandlerArgs) => {
  const {isEnabled, isEnabledBeforeInteraction, onBeforeRequest, onSuccess, onError, request} = args;
  try {
    let isRequestSent = false;
    if (isEnabled !== isEnabledBeforeInteraction) {
      if (onBeforeRequest) {
        onBeforeRequest(isEnabled);
      }
      await request(isEnabled);
      isRequestSent = true;
    }
    if (onSuccess) {
      onSuccess(isRequestSent);
    }
  } catch (error) {
    if (is401Error(error)) {
      await handleRefreshToken(error, handleApiRequest, args);
      return;
    }

    onError?.(error);
  }
}, DEBOUNCE_DELAY_MS);

const useSettingApi = (
  settingControllerName: SettingControllerName,
  apiRequest: (isEnabled: boolean) => Promise<void>,
) => {
  const [isActive, setIsActive] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isActiveBeforeInteractionRef = useRef<boolean | null>(null);

  const handleChange = useCallback(() => {
    const prevIsActive = isActive;
    const nextIsActive = !prevIsActive;

    if (isActiveBeforeInteractionRef.current === null) {
      isActiveBeforeInteractionRef.current = prevIsActive;
    }

    setIsActive(nextIsActive);
    setHasError(false);

    handleApiRequest({
      request: apiRequest,
      isEnabled: nextIsActive,
      isEnabledBeforeInteraction: isActiveBeforeInteractionRef.current,
      onBeforeRequest: (acceptedIsActive: boolean) => {
        // Before sending the request we want to update the ref to the last accepted state
        // If we wont do that, in case of multiple rapid toggles, the ref will hold an outdated value and some requests
        // might not be sent which will lead to inconsistent state between client and server
        // If the code reaches here, it means that the request is about to be sent and the accepted state is the
        // current one
        isActiveBeforeInteractionRef.current = acceptedIsActive;
      },
      onSuccess: (isRequestSent: boolean) => {
        isActiveBeforeInteractionRef.current = null;
        if (isRequestSent) {
          trackEvent('settingsOption_change', {
            settingControllerName,
            settingsAction: nextIsActive ? SettingsAction.On : SettingsAction.Off,
          });
        }
      },
      onError: () => {
        isActiveBeforeInteractionRef.current = null;

        setIsActive(prevIsActive);
        setHasError(true);
      },
    });
  }, [apiRequest, isActive, settingControllerName]);

  return {isActive, setIsActive, hasError, handleChange};
};

export default useSettingApi;
